/**
 * Commonly used errors throught the API.
 * Internal errors have a Name, a Message and a Status
 *
 * We need to define errors like this because of Typescript
 * (see https://github.com/Microsoft/TypeScript/issues/13965)
 *
 * Validation errors are usually generated by express-validator, and the
 * `fields` parameter corresponds to the result.array() that is returned by
 * `req.getValidationResult()`.
 *
 * It is possible to generate a fields array manually. It must contain objects
 * with the following structure:
 * {
 *    param: "field-name",
 *    msg: "Error message",
 * }
 *
 *
 * `ClientError` is a special type since it only happens on the client
 * side. Examples are connection refused, or invalid axios configuration, etc.
 *
 */

/* eslint-disable max-classes-per-file */

type ErrorKind =
  | 'ValidationError'
  | 'BadRequestError'
  | 'AuthorizationError'
  | 'PermissionError'
  | 'ResourceNotFoundError'
  | 'InternalError'
  | 'ServerUnavailableError'
  | 'ApiVersionMismatchError'
  | 'ClientError'
  | 'PluginInitializationError'
  | 'PluginAuthorizationError'
  | 'ConflictError';

export class ApplicationError extends Error {
  public code: number;
  public kind: ErrorKind;
  public __proto__: Error;

  constructor(message: string, code: number, kind: ErrorKind) {
    const proto = new.target.prototype;
    super(message);
    this.__proto__ = proto;
    this.code = code;
    this.kind = kind;
  }

  public toJson(): {} {
    return { kind: this.kind, code: this.code, message: this.message };
  }
}

export class ValidationError extends ApplicationError {
  constructor(
    message: string,
    public fields: any[]
  ) {
    super(message, 400, 'ValidationError');
  }

  public toJson(): {} {
    return {
      fields: this.fields.map(f => {
        return { param: f.param, value: f.value, msg: f.msg };
      }),
      ...super.toJson(),
    };
  }
}

export class BadRequestError extends ApplicationError {
  constructor(message: string) {
    super(message, 400, 'BadRequestError');
  }
}

export class ConflictError extends ApplicationError {
  constructor(message: string) {
    super(message, 409, 'ConflictError');
  }
}

export class AuthorizationError extends ApplicationError {
  constructor(
    message: string,
    public subtype?: string
  ) {
    super(message, 401, 'AuthorizationError');
  }

  public toJson(): {} {
    return {
      subtype: this.subtype,
      ...super.toJson(),
    };
  }
}

export class PermissionError extends ApplicationError {
  constructor(message: string) {
    super(message, 403, 'PermissionError');
  }
}

export class ResourceNotFoundError extends ApplicationError {
  constructor(message: string) {
    super(message, 404, 'ResourceNotFoundError');
  }
}

export class ApiVersionMismatchError extends ApplicationError {
  constructor(message: string) {
    super(message, 412, 'ApiVersionMismatchError');
  }
}

export class InternalError extends ApplicationError {
  constructor(message: string) {
    super(message, 500, 'InternalError');
  }
}

export class ServerUnavailableError extends ApplicationError {
  constructor(message: string) {
    super(message, 503, 'ServerUnavailableError');
  }
}

export class ClientError extends ApplicationError {
  constructor(message: string) {
    super(message, 0, 'ClientError');
  }
}

export class PluginInitializationError extends ApplicationError {
  constructor(pluginName: string, message: string) {
    super(`[${pluginName}]: ${message}`, 0, 'PluginInitializationError');
  }
}

export class PluginAuthorizationError extends ApplicationError {
  constructor(pluginName: string, message: string) {
    super(`[${pluginName}]: ${message}`, 401, 'PluginAuthorizationError');
  }
}

export function fromObject(obj: any): ApplicationError {
  if (!obj.message) {
    return new InternalError('Internal Error');
  }

  let kind = obj.kind as ErrorKind;

  switch (kind) {
    case 'InternalError':
      return new InternalError(obj.message);
    case 'ServerUnavailableError':
      return new ServerUnavailableError(obj.message);
    case 'ApiVersionMismatchError':
      return new ApiVersionMismatchError(obj.message);
    case 'ValidationError':
      return new ValidationError(obj.message, obj.fields);
    case 'BadRequestError':
      return new BadRequestError(obj.message);
    case 'AuthorizationError':
      return new AuthorizationError(obj.message);
    case 'PermissionError':
      return new PermissionError(obj.message);
    case 'ResourceNotFoundError':
      return new ResourceNotFoundError(obj.message);
    case 'ClientError':
      return new ClientError(obj.message);
    case 'PluginInitializationError':
      return new PluginInitializationError(obj.pluginName, obj.message);
    case 'PluginAuthorizationError':
      return new PluginAuthorizationError(obj.pluginName, obj.message);
    case 'ConflictError':
      return new ConflictError(obj.message);
    default:
      const exhaustiveCheck: never = kind;
      return exhaustiveCheck;
  }
}

// FIXME: Improve this, because it makes too many assumptions about the error being an Axios
// error. Instead we should use `axios.isAxiosError` and `getErrorMessage` if necessary
// Based on https://github.com/axios/axios#handling-errors
export function fromApiError(error: any): ApplicationError {
  if (error.response) {
    let response = error.response;

    let errorMessage;
    if (response.data && response.data.message) {
      errorMessage = response.data.message;
    } else if (response.statusText) {
      errorMessage = response.statusText;
    } else {
      errorMessage = error.message;
    }

    switch (response.status) {
      case 400:
        return response.data.fields
          ? new ValidationError(errorMessage, response.data.fields)
          : new BadRequestError(errorMessage);
      case 401:
        return new AuthorizationError(errorMessage);
      case 403:
        return new PermissionError(errorMessage);
      case 404:
        return new ResourceNotFoundError(errorMessage);
      case 412:
        return new ApiVersionMismatchError(errorMessage);
      case 503:
        return new ServerUnavailableError(errorMessage);
      default:
        return new InternalError(errorMessage);
    }
  } else {
    // This could mean a request error (e.g. connection refused), or an invalid
    // axios configuration. Eventually, we should separate the two of them (the
    // discriminator being whether error.request exists for the former)
    return new ClientError(error.message);
  }
}

// Utilities to work with errors in typescript. The main issue is we cannot use things like
// try { ... } catch(e) { log(e.message) }, because in JS we can throw anything (i.e., things
// that are not errors!

// From https://kentcdodds.com/blog/get-a-catch-block-error-message-with-typescript
interface ErrorWithMessage {
  message: string;
}

/**
 * Ensure an unknown parameter is an Error with a string message and narrow the type
 */
function isErrorWithMessage(error: unknown): error is ErrorWithMessage {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as Record<string, unknown>).message === 'string'
  );
}

/**
 * Convert an unknown error type to an actual Error
 */
function toErrorWithMessage(maybeError: unknown): ErrorWithMessage {
  if (isErrorWithMessage(maybeError)) return maybeError;

  try {
    return new Error(JSON.stringify(maybeError));
  } catch {
    // fallback in case there's an error stringifying the maybeError
    // like with circular references for example.
    return new Error(String(maybeError));
  }
}

/**
 * Extract the message attribute from a potential error object
 */
export function getErrorMessage(error: unknown) {
  return toErrorWithMessage(error).message;
}
